import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import PatternFinderPage from "@/pages/PatternFinderPage";
import BookingSuccess from "@/pages/BookingSuccess";
import BookingCancel from "@/pages/BookingCancel";
import AdminPanel from "@/pages/AdminPanel";
import IntakeForm from "@/pages/IntakeForm";
import ReschedulePage from "@/pages/ReschedulePage";
import CancelPage from "@/pages/CancelPage";
import ReviewPage from "@/pages/ReviewPage";
import { PortalAuthProvider } from "@/context/PortalAuthContext";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import ProtectedRoute from "@/components/portal/ProtectedRoute";
import { PortalLogin, PortalRegister } from "@/pages/portal/PortalAuth";
import { ForgotPassword, ResetPassword } from "@/pages/portal/PasswordReset";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import OnboardingPage from "@/pages/portal/OnboardingPage";
import SharedReportPage from "@/pages/portal/SharedReportPage";
import ProgressPage from "@/pages/portal/ProgressPage";
import ReferPage from "@/pages/portal/ReferPage";
import BigFivePage from "@/pages/portal/BigFivePage";
import DarkTriadPage from "@/pages/portal/DarkTriadPage";
import NewIncidentPage from "@/pages/portal/NewIncidentPage";
import IncidentDetailPage from "@/pages/portal/IncidentDetailPage";
import VulnerabilityReportPage from "@/pages/portal/VulnerabilityReportPage";
import PricingPage from "@/pages/portal/PricingPage";
import UpgradeSuccessPage from "@/pages/portal/UpgradeSuccessPage";
import AuthCallback from "@/pages/AuthCallback";
import LegalPage from "@/pages/LegalPage";

const protectedRoutes = [
  { path: "/portal", element: <PortalDashboard /> },
  { path: "/portal/onboarding", element: <OnboardingPage /> },
  { path: "/portal/big-five", element: <BigFivePage /> },
  { path: "/portal/incident/new", element: <NewIncidentPage /> },
  { path: "/portal/incident/:id", element: <IncidentDetailPage /> },
  { path: "/portal/incident/:id/leader-d3", element: <DarkTriadPage /> },
  { path: "/portal/report", element: <VulnerabilityReportPage /> },
  { path: "/portal/progress", element: <ProgressPage /> },
  { path: "/portal/refer", element: <ReferPage /> },
  { path: "/portal/pricing", element: <PricingPage /> },
  { path: "/portal/upgrade/success", element: <UpgradeSuccessPage /> },
];

function AppRoutes() {
  const location = useLocation();
  // Google sign-in returns to {origin}{path}#session_id=... — handle it before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pattern-finder" element={<PatternFinderPage />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/cancel" element={<BookingCancel />} />
      <Route path="/intake-form" element={<IntakeForm />} />
      <Route path="/reschedule/:bookingId" element={<ReschedulePage />} />
      <Route path="/cancel/:bookingId" element={<CancelPage />} />
      <Route path="/review/:bookingId" element={<ReviewPage />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/refunds" element={<LegalPage doc="refunds" />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/forgot-password" element={<ForgotPassword />} />
      <Route path="/portal/reset-password" element={<ResetPassword />} />
      <Route path="/shared-report/:token" element={<SharedReportPage />} />
      {protectedRoutes.map(({ path, element }) => (
        <Route key={path} path={path} element={<ProtectedRoute>{element}</ProtectedRoute>} />
      ))}
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#020617]">
      <div className="noise-overlay" />
      <BrowserRouter>
        <PortalAuthProvider>
          <AnalyticsRouteTracker />
          <AppRoutes />
        </PortalAuthProvider>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#0F172A',
            border: '1px solid #1E293B',
            color: '#F8FAFC',
          },
        }}
      />
    </div>
  );
}

export default App;
